import React, { useState, useEffect, useMemo } from 'react';
import GroupForm from './GroupForm';
import { PageHeader } from './common';
import { API_ENDPOINTS } from '../utils/apiConfig';
import { useNotifications } from '../contexts/NotificationContext';

const PAGE_SIZE = 24; // Nombre de groupes par page

const GROUP_TYPE_LABELS = {
  prestation: 'Prestation',
  interne: 'Interne'
};

const GROUP_TYPE_COLORS = {
  prestation: { bg: '#e3f2fd', color: '#1565c0' },
  interne: { bg: '#fff3e0', color: '#e65100' }
};

const GroupsList = ({ token, onOpenMembersManager, onNavigateToUsers }) => {
  const { showSuccess, showError } = useNotifications();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', onConfirm: null });

  const loadGroups = async () => {
    try {
      setLoading(true);
      const response = await fetch(API_ENDPOINTS.GROUPS, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        setGroups(data.data);
        setError('');
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const deleteGroup = (groupId, groupName) => {
    setConfirmModal({
      show: true,
      title: 'Supprimer le groupe',
      message: `Êtes-vous sûr de vouloir supprimer le groupe "${groupName}" ?`,
      onConfirm: async () => {
        setConfirmModal({ ...confirmModal, show: false });
        try {
          const response = await fetch(API_ENDPOINTS.GROUP_BY_ID(groupId), {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          const data = await response.json();

          if (data.success) {
            await loadGroups();
            showSuccess('Groupe supprimé');
          } else {
            showError('Erreur: ' + data.error);
          }
        } catch (err) {
          showError('Erreur de connexion au serveur');
        }
      }
    });
  };

  const filteredGroups = useMemo(() => {
    return groups.filter(group => {
      // Filtre par type
      if (typeFilter !== 'all' && group.group_type !== typeFilter) {
        return false;
      }
      // Filtre par recherche texte
      return group.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
             group.code?.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [groups, searchTerm, typeFilter]);

  // Pagination
  const totalPages = Math.ceil(filteredGroups.length / PAGE_SIZE);
  const paginatedGroups = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredGroups.slice(start, start + PAGE_SIZE);
  }, [filteredGroups, currentPage]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, typeFilter]);

  useEffect(() => {
    loadGroups();
  }, [token]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <p>Chargement des groupes...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <PageHeader
        icon="🚛"
        title="Groupes de Travail"
        color="purple"
        actions={
          <>
            {onNavigateToUsers && (
              <button
                onClick={onNavigateToUsers}
                style={{
                  background: 'transparent',
                  color: '#28a745',
                  border: '2px solid #28a745',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '14px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#28a745';
                  e.target.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = '#28a745';
                }}
              >
                Utilisateurs
              </button>
            )}
            {onOpenMembersManager && (
              <button
                onClick={onOpenMembersManager}
                style={{
                  background: 'linear-gradient(135deg, #6f42c1, #5a32a3)',
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <span>👥</span>
                Gérer les membres
              </button>
            )}
            <button
              onClick={() => { setEditingGroup(null); setShowForm(true); }}
              style={{
                background: 'linear-gradient(135deg, #28a745, #20c997)',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              + Nouveau groupe
            </button>
          </>
        }
      />

      {error && (
        <div style={{ background: '#f8d7da', color: '#721c24', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
          {error}
        </div>
      )}

      {/* Recherche et filtres */}
      <div style={{
        background: 'white',
        padding: '20px',
        borderRadius: '12px',
        marginBottom: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        display: 'flex',
        gap: '16px',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        <input
          type="text"
          placeholder="Rechercher un groupe (nom ou code)..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            flex: '1',
            minWidth: '200px',
            padding: '14px',
            border: '2px solid #e1e5e9',
            borderRadius: '8px',
            fontSize: '14px',
            boxSizing: 'border-box'
          }}
        />
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          style={{
            padding: '14px 16px',
            border: '2px solid #e1e5e9',
            borderRadius: '8px',
            fontSize: '14px',
            background: 'white',
            cursor: 'pointer',
            minWidth: '180px'
          }}
        >
          <option value="all">Tous les types</option>
          <option value="prestation">Prestations uniquement</option>
          <option value="interne">Internes uniquement</option>
        </select>
      </div>

      {/* Liste des groupes */}
      {filteredGroups.length === 0 ? (
        <div style={{
          background: 'white',
          padding: '60px 20px',
          borderRadius: '12px',
          textAlign: 'center',
          color: '#6c757d'
        }}>
          {searchTerm ? 'Aucun groupe trouvé' : 'Aucun groupe créé'}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
          {paginatedGroups.map(group => (
            <div
              key={group.id}
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '2px solid #e9ecef',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onClick={() => { setEditingGroup(group); setShowForm(true); }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div style={{
                  background: group.group_type === 'interne' ? '#e65100' : '#6f42c1',
                  color: 'white',
                  width: '50px',
                  height: '50px',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px',
                  fontWeight: 'bold'
                }}>
                  {group.code}
                </div>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <span style={{
                    background: GROUP_TYPE_COLORS[group.group_type]?.bg || '#e3f2fd',
                    color: GROUP_TYPE_COLORS[group.group_type]?.color || '#1565c0',
                    padding: '4px 10px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: '600'
                  }}>
                    {GROUP_TYPE_LABELS[group.group_type] || 'Prestation'}
                  </span>
                  <span style={{
                    background: group.is_active ? '#d4edda' : '#f8d7da',
                    color: group.is_active ? '#155724' : '#721c24',
                    padding: '4px 10px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: '600'
                  }}>
                    {group.is_active ? 'Actif' : 'Inactif'}
                  </span>
                </div>
              </div>

              <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: '#333' }}>
                {group.name}
              </h3>

              <div style={{ fontSize: '13px', color: '#6c757d', marginBottom: '16px' }}>
                {group.members_count || 0} membre{(group.members_count || 0) !== 1 ? 's' : ''}
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={(e) => { e.stopPropagation(); setEditingGroup(group); setShowForm(true); }}
                  style={{
                    flex: 1,
                    background: '#007bff',
                    color: 'white',
                    border: 'none',
                    padding: '10px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  Modifier
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); deleteGroup(group.id, group.name); }}
                  style={{
                    flex: 1,
                    background: '#dc3545',
                    color: 'white',
                    border: 'none',
                    padding: '10px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  Supprimer
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '10px',
          marginTop: '24px',
          padding: '16px',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <button
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            style={{
              padding: '8px 12px',
              background: currentPage === 1 ? '#e9ecef' : '#6f42c1',
              color: currentPage === 1 ? '#6c757d' : 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              fontWeight: '600'
            }}
          >
            ««
          </button>
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            style={{
              padding: '8px 16px',
              background: currentPage === 1 ? '#e9ecef' : '#6f42c1',
              color: currentPage === 1 ? '#6c757d' : 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              fontWeight: '600'
            }}
          >
            Précédent
          </button>

          <span style={{ padding: '0 16px', fontWeight: '600', color: '#495057' }}>
            Page {currentPage} / {totalPages}
          </span>
          <span style={{ color: '#6c757d', fontSize: '14px' }}>
            ({filteredGroups.length} groupes)
          </span>

          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            style={{
              padding: '8px 16px',
              background: currentPage === totalPages ? '#e9ecef' : '#6f42c1',
              color: currentPage === totalPages ? '#6c757d' : 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              fontWeight: '600'
            }}
          >
            Suivant
          </button>
          <button
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
            style={{
              padding: '8px 12px',
              background: currentPage === totalPages ? '#e9ecef' : '#6f42c1',
              color: currentPage === totalPages ? '#6c757d' : 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              fontWeight: '600'
            }}
          >
            »»
          </button>
        </div>
      )}

      {/* Formulaire modal */}
      {showForm && (
        <GroupForm
          token={token}
          group={editingGroup}
          onClose={() => setShowForm(false)}
          onSave={() => {
            setShowForm(false);
            loadGroups();
          }}
        />
      )}

      {/* Modal de confirmation */}
      {confirmModal.show && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 2000, padding: '20px'
        }}>
          <div style={{
            background: 'white', borderRadius: '16px', padding: '24px',
            maxWidth: '400px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '600' }}>{confirmModal.title}</h3>
            <p style={{ margin: '0 0 24px 0', fontSize: '14px', color: '#666' }}>{confirmModal.message}</p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirmModal({ ...confirmModal, show: false })}
                style={{ padding: '10px 20px', background: '#f5f5f5', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
                Annuler
              </button>
              <button onClick={confirmModal.onConfirm}
                style={{ padding: '10px 20px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}>
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupsList;
